import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { protocolDisplayName } from "@brewbase/lib";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Search,
  PlusIcon,
  LockIcon,
  CopyIcon,
  ChevronDown,
  ChevronRight,
  ArchiveIcon,
  ArchiveRestoreIcon,
  FilesIcon,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { formatDate } from "@/lib/date";
import type { ProtocolListItem } from "@/features/fermentation-protocol-editor";
import { z } from "zod";
import { useZodForm } from "@/components/form";
import type { FermentationProtocolId } from "@brewbase/db/types";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";

export const Route = createFileRoute("/fermentations/protocols/")({
  component: RouteComponent,
});

const newProtocolSchema = z.object({
  name: z.string().min(1, "Name is required"),
  version: z.string().min(1, "Version is required"),
  description: z.string().optional(),
  sourceProtocolId: z.number().nullable(),
});

function RouteComponent() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedProtocols, setExpandedProtocols] = useState<Set<string>>(
    new Set(),
  );
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [tab, setTab] = useState<"active" | "archived">("active");

  const { data: protocols, isLoading } =
    trpc.fermentations.protocols.list.useQuery({
      includeArchived: tab === "archived",
    });

  const { data: archivedCount } =
    trpc.fermentations.protocols.countArchived.useQuery();

  const createMutation = trpc.fermentations.protocols.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Protocol "${data.name}" created!`);
      utils.fermentations.protocols.list.invalidate();
      setIsNewDialogOpen(false);
      navigate({
        to: "/fermentations/protocols/$protocol_id/overview",
        params: { protocol_id: data.id },
      });
    },
    onError: (error) => {
      toast.error(`Failed to create protocol: ${error.message}`);
    },
  });

  const createNewVersionMutation =
    trpc.fermentations.protocols.createNewVersion.useMutation({
      onSuccess: (data) => {
        toast.success(`New version "${data.version}" created!`);
        utils.fermentations.protocols.list.invalidate();
        navigate({
          to: "/fermentations/protocols/$protocol_id/overview",
          params: { protocol_id: data.id },
        });
      },
      onError: (error) => {
        toast.error(`Failed to create new version: ${error.message}`);
      },
    });

  const duplicateMutation = trpc.fermentations.protocols.duplicate.useMutation({
    onSuccess: (data) => {
      toast.success(`Protocol "${data.name}" created!`);
      utils.fermentations.protocols.list.invalidate();
      setIsNewDialogOpen(false);
      navigate({
        to: "/fermentations/protocols/$protocol_id/overview",
        params: { protocol_id: data.id },
      });
    },
    onError: (error) => {
      toast.error(`Failed to create protocol: ${error.message}`);
    },
  });

  const deleteMutation = trpc.fermentations.protocols.delete.useMutation({
    onSuccess: () => {
      toast.success("Protocol deleted.");
      utils.fermentations.protocols.list.invalidate();
      utils.fermentations.protocols.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to delete protocol: ${error.message}`);
    },
  });

  const archiveMutation = trpc.fermentations.protocols.archive.useMutation({
    onSuccess: () => {
      toast.success("Protocol archived.");
      utils.fermentations.protocols.list.invalidate();
      utils.fermentations.protocols.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to archive: ${error.message}`);
    },
  });

  const unarchiveMutation = trpc.fermentations.protocols.unarchive.useMutation({
    onSuccess: () => {
      toast.success("Protocol unarchived.");
      utils.fermentations.protocols.list.invalidate();
      utils.fermentations.protocols.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to unarchive: ${error.message}`);
    },
  });

  const form = useZodForm({
    schema: newProtocolSchema,
    defaultValues: {
      name: "",
      version: "1.0",
      description: "",
      sourceProtocolId: null,
    },
  });

  const openNewDialog = (source?: ProtocolListItem) => {
    form.reset({
      name: source ? `${source.name} (copy)` : "",
      version: "1.0",
      description: source?.description ?? "",
      sourceProtocolId: source?.id ?? null,
    });
    setIsNewDialogOpen(true);
  };

  // Global protocols one can start from. The active list is already loaded;
  // the archived list is not worth a second query for a template picker.
  const sourceOptions = useMemo(
    () =>
      (protocols ?? [])
        .filter((p) => !p.archived_at)
        .sort(
          (a, b) =>
            protocolDisplayName(a.name).localeCompare(
              protocolDisplayName(b.name),
            ) || (b.version ?? "").localeCompare(a.version ?? ""),
        ),
    [protocols],
  );

  const toggleExpanded = (protocolName: string) => {
    setExpandedProtocols((prev) => {
      const next = new Set(prev);
      if (next.has(protocolName)) {
        next.delete(protocolName);
      } else {
        next.add(protocolName);
      }
      return next;
    });
  };

  // Group protocols by name (for versioning)
  const groupedProtocols: Record<string, ProtocolListItem[]> = useMemo(() => {
    if (!protocols) return {};

    const filtered = protocols.filter((p) => {
      // Filter by tab
      const isArchived = !!p.archived_at;
      if (tab === "archived" && !isArchived) return false;
      if (tab === "active" && isArchived) return false;

      const searchLower = searchQuery.toLowerCase();
      return (
        protocolDisplayName(p.name).toLowerCase().includes(searchLower) ||
        (p.version || "").toLowerCase().includes(searchLower) ||
        (p.description || "").toLowerCase().includes(searchLower)
      );
    });

    return filtered.reduce(
      (acc, protocol) => {
        const key = protocolDisplayName(protocol.name);
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(protocol);
        return acc;
      },
      {} as Record<string, ProtocolListItem[]>,
    );
  }, [protocols, searchQuery, tab]);

  const handleCreateProtocol = form.handleSubmit((values) => {
    if (values.sourceProtocolId != null) {
      duplicateMutation.mutate({
        sourceProtocolId: values.sourceProtocolId as FermentationProtocolId,
        name: values.name,
        version: values.version,
        description: values.description || null,
      });
      return;
    }
    createMutation.mutate({
      name: values.name,
      version: values.version,
      description: values.description || null,
    });
  });
  const isCreating = createMutation.isPending || duplicateMutation.isPending;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Loading protocols...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search protocols..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button onClick={() => openNewDialog()}>
          <PlusIcon className="w-4 h-4 mr-1" />
          New Protocol
        </Button>
        <Dialog open={isNewDialogOpen} onOpenChange={setIsNewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Protocol</DialogTitle>
              <DialogDescription>
                Create a new fermentation protocol with runtime parameters,
                empty or copied from an existing protocol as a starting point.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateProtocol}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="source">Start from</Label>
                  <Select
                    value={String(form.watch("sourceProtocolId") ?? "none")}
                    onValueChange={(v) => {
                      const id = v === "none" ? null : Number(v);
                      form.setValue("sourceProtocolId", id);
                      const src = sourceOptions.find((p) => p.id === id);
                      if (src && !form.getValues("name")) {
                        form.setValue("name", `${src.name} (copy)`);
                      }
                    }}
                  >
                    <SelectTrigger id="source">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Empty protocol</SelectItem>
                      {sourceOptions.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {protocolDisplayName(p.name)}
                          {p.version ? ` v${p.version}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., High-Temperature"
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input
                      id="version"
                      placeholder="e.g., 1.0"
                      {...form.register("version")}
                    />
                    {form.formState.errors.version && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.version.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description..."
                    {...form.register("description")}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsNewDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating}>
                  {isCreating ? "Creating..." : "Create Protocol"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <SlimTabs
        value={tab}
        onValueChange={(v) => setTab(v as "active" | "archived")}
        className="mb-4"
      >
        <SlimTabsList>
          <SlimTabsTrigger value="active">
            Active ({tab === "active" ? Object.values(groupedProtocols).flat().length : "..."})
          </SlimTabsTrigger>
          <SlimTabsTrigger value="archived">
            Archived ({archivedCount ?? 0})
          </SlimTabsTrigger>
        </SlimTabsList>
      </SlimTabs>

      {/* Protocol list */}
      <div className="divide-y divide-border rounded-md border">
        {Object.entries(groupedProtocols)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, versions]) => (
            <ProtocolGroup
              key={name}
              name={name}
              versions={versions}
              searchQuery={searchQuery}
              expanded={expandedProtocols.has(name)}
              onToggle={() => toggleExpanded(name)}
              onCreateVersion={(id) =>
                // No version given: the server picks the next free one,
                // skipping archived versions the list hides.
                createNewVersionMutation.mutate({
                  sourceProtocolId: id as FermentationProtocolId,
                })
              }
              isCreatingVersion={createNewVersionMutation.isPending}
              onDuplicate={(protocol) => openNewDialog(protocol)}
              onDelete={(id) =>
                deleteMutation.mutate(id as FermentationProtocolId)
              }
              deletingId={deleteMutation.isPending ? deleteMutation.variables : undefined}
              tab={tab}
              onArchive={(id) =>
                archiveMutation.mutate(id as FermentationProtocolId)
              }
              onUnarchive={(id) =>
                unarchiveMutation.mutate(id as FermentationProtocolId)
              }
              isArchiving={archiveMutation.isPending || unarchiveMutation.isPending}
            />
          ))}

        {/* Empty state */}
        {Object.keys(groupedProtocols).length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-3 text-sm">
              {searchQuery
                ? "No protocols found matching your search."
                : "No protocols yet. Create your first one!"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ======================== Protocol Group ========================

interface ProtocolGroupProps {
  name: string;
  versions: ProtocolListItem[];
  searchQuery: string;
  expanded: boolean;
  onToggle: () => void;
  onCreateVersion: (id: number) => void;
  isCreatingVersion: boolean;
  onDuplicate: (protocol: ProtocolListItem) => void;
  onDelete: (id: number) => void;
  deletingId: number | undefined;
  tab: "active" | "archived";
  onArchive: (id: number) => void;
  onUnarchive: (id: number) => void;
  isArchiving: boolean;
}

function ProtocolGroup({
  name,
  versions,
  searchQuery,
  expanded,
  onToggle,
  onCreateVersion,
  isCreatingVersion,
  onDuplicate,
  onDelete,
  deletingId,
  tab,
  onArchive,
  onUnarchive,
  isArchiving,
}: ProtocolGroupProps) {
  const hasMultipleVersions = versions.length > 1;
  const latestVersion = versions[0];

  const versionsToShow =
    searchQuery || !hasMultipleVersions
      ? versions
      : expanded
        ? versions
        : [latestVersion];

  return (
    <div>
      {/* Protocol group header (if multiple versions) */}
      {hasMultipleVersions && !searchQuery && (
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium">{name}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {versions.length} versions
          </span>
        </button>
      )}

      {/* Version rows */}
      <div className={hasMultipleVersions && !searchQuery ? "pl-6" : ""}>
        {versionsToShow.map((protocol) => (
          <div
            key={protocol.id}
            className="group flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link
                  to="/fermentations/protocols/$protocol_id/overview"
                  params={{ protocol_id: protocol.id }}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {hasMultipleVersions || searchQuery
                    ? `v${protocol.version}`
                    : protocol.name}
                </Link>

                {protocol.mode === "CONTINUOUS" && (
                  <Badge
                    variant="outline"
                    className="text-xs border-indigo-300 text-indigo-700"
                  >
                    Continuous
                  </Badge>
                )}

                {protocol.is_locked && (
                  <Badge
                    variant="outline"
                    className="gap-1 text-warning border-warning"
                  >
                    <LockIcon className="h-3 w-3" />
                    Locked
                  </Badge>
                )}
              </div>

              {protocol.description && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {protocol.description}
                </p>
              )}

              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span>Created {formatDate(protocol.created_at)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {tab === "active" ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCreateVersion(protocol.id)}
                    disabled={isCreatingVersion}
                    title="Create new version"
                  >
                    <CopyIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDuplicate(protocol)}
                    title="New protocol from this one"
                  >
                    <FilesIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onArchive(protocol.id)}
                    disabled={isArchiving}
                    title="Archive"
                  >
                    <ArchiveIcon className="h-3 w-3" />
                  </Button>
                  <DeleteConfirmDialog
                    title="Delete Protocol"
                    description={`Delete "${protocol.name} v${protocol.version}"? This cannot be undone if the protocol has been used in a fermentation.`}
                    onConfirm={() => onDelete(protocol.id)}
                    isDeleting={deletingId === protocol.id}
                    disabled={protocol.is_locked}
                  />
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUnarchive(protocol.id)}
                    disabled={isArchiving}
                    title="Unarchive"
                  >
                    <ArchiveRestoreIcon className="h-3 w-3" />
                  </Button>
                  <DeleteConfirmDialog
                    title="Delete Protocol"
                    description={`Delete "${protocol.name} v${protocol.version}"? This cannot be undone.`}
                    onConfirm={() => onDelete(protocol.id)}
                    isDeleting={deletingId === protocol.id}
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
